
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { AuditLog } from '../../types';
import { Search, Clock, User, ChevronLeft, ChevronRight } from 'lucide-react';

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (isSupabaseConfigured()) {
        supabase.from('audit_logs').select('*, profiles(full_name, email)').order('created_at', { ascending: false }).limit(200)
          .then(({data}) => data && setLogs(data as unknown as AuditLog[]));
    }
    setLoading(false);
  }, []);

  const filteredLogs = logs.filter(log => log.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || log.action.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const currentLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="flex flex-col h-full overflow-hidden">
        <div className="p-4 bg-white border-b border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
            <div className="relative flex-1 max-w-sm w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input type="text" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="Filtrer..." className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50"><ChevronLeft size={20}/></button>
                <span className="text-sm font-medium">{currentPage} / {totalPages || 1}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50"><ChevronRight size={20}/></button>
            </div>
        </div>

        <div className="flex-1 overflow-auto relative bg-white">
            <table className="w-full text-left text-sm text-slate-600 border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-900 font-bold border-b-2 border-slate-200 shadow-sm">
                    <tr>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Date & Heure</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Utilisateur</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Action</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Entité</th>
                        <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Détails</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {currentLogs.map(log => (
                        <tr key={log.id} className="hover:bg-slate-50">
                            <td className="px-6 py-3 font-mono text-xs whitespace-nowrap"><div className="flex items-center gap-2"><Clock size={14} className="text-slate-400"/>{new Date(log.created_at).toLocaleString()}</div></td>
                            <td className="px-6 py-3"><div className="flex items-center gap-2"><User size={14} className="text-slate-400"/><span className="font-medium text-slate-900">{log.profiles?.full_name || 'Système'}</span></div></td>
                            <td className="px-6 py-3"><span className="px-2 py-1 rounded text-[10px] font-bold uppercase border bg-slate-50">{log.action}</span></td>
                            <td className="px-6 py-3 font-mono text-xs text-slate-500">{log.entity}</td>
                            <td className="px-6 py-3 text-xs truncate max-w-xs">{JSON.stringify(log.details)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
  );
};

export default AuditLogs;
